import { Kbd, KbdGroup } from '@repo/ui/kbd'
import { Fragment } from 'react'
import { useTranslation } from '@/lib/i18n'
import {
	currentPlatform,
	formatShortcut,
	SHORTCUTS,
	type ShortcutId
} from '@/modules/shortcuts/lib/shortcuts'

/**
 * A shortcut drawn as keycaps, from the registry, so a tooltip can never
 * advertise a key the dispatcher does not answer to. The first variant by
 * default — a tooltip has room for one — and every variant with `all`.
 */
export function ShortcutHint({
	all,
	id,
	plain
}: {
	all?: boolean
	id: ShortcutId
	plain?: boolean
}) {
	const { t } = useTranslation()
	const platform = currentPlatform()
	const specs = SHORTCUTS[id][platform]

	// Plain small text, for a menu row where keycaps read too loud.
	if (plain) {
		return (
			<>
				{(all ? specs : specs.slice(0, 1))
					.map((spec) =>
						formatShortcut(spec, platform)
							.map((step) => step.join(' '))
							.join(` ${t('client:shortcuts.then')} `)
					)
					.join(` ${t('client:shortcuts.or')} `)}
			</>
		)
	}

	return (
		<span className='inline-flex items-center gap-1'>
			{(all ? specs : specs.slice(0, 1)).map((spec, index) => (
				<Fragment key={spec}>
					{index > 0 && (
						<span className='text-muted-foreground text-xs'>
							{t('client:shortcuts.or')}
						</span>
					)}
					{formatShortcut(spec, platform).map((step, stepIndex) => (
						<Fragment key={step.join('+')}>
							{stepIndex > 0 && (
								<span className='text-muted-foreground text-xs'>
									{t('client:shortcuts.then')}
								</span>
							)}
							<KbdGroup>
								{step.map((cap) => (
									<Kbd key={cap}>{cap}</Kbd>
								))}
							</KbdGroup>
						</Fragment>
					))}
				</Fragment>
			))}
		</span>
	)
}
